/* components/ui/Metric.jsx — метрики и индикаторы квоты.
   Stat        — значение + подпись + опц. дельта; число Geist Mono tabular.
   Metric      — карточка метрики: иконка + значение + подпись (KPI-бары).
   ExtraMetric — компактная inline «ключ: значение» для мета-строк карточек.
   MiniBar     — горизонтальный прогресс квоты (used/limit); >80% меняет цвет.
   UsageBar    — расширенный MiniBar с подписями и цифрами (раздел подписки).
   ScreenHeader— герой-заголовок auth/онбординг: serif/sans, align, eyebrow.
   ХЕНДОФФ → frontend/components/common/. */

import { Icon } from './Icon.jsx';

  /* ── Stat ── */
  function Stat({ value, label, delta, deltaTone, align = 'left', size = 'md', className }) {
    const tone = deltaTone || (delta == null ? null : delta > 0 ? 'ok' : delta < 0 ? 'bad' : 'neutral');
    return (
      <div className={'stat stat--' + size + ' stat--' + align + (className ? ' ' + className : '')}>
        <div className="stat__value num">{value}</div>
        <div className="stat__label t-caption">{label}</div>
        {delta != null && (
          <div className={'stat__delta stat__delta--' + tone}>
            <Icon name={delta > 0 ? 'arrow-up-right' : 'arrow-right'} size={13} />
            <span className="num">{typeof delta === 'number' ? (delta > 0 ? '+' : '') + delta + '%' : delta}</span>
          </div>
        )}
      </div>
    );
  }

  /* ── Metric (карточка) ── */
  function Metric({ icon, value, label, tone = 'accent', className }) {
    return (
      <div className={'metric' + (className ? ' ' + className : '')}>
        {icon && <span className={'metric__icon metric__icon--' + tone}><Icon name={icon} size={18} /></span>}
        <div className="metric__body">
          <div className="metric__value num">{value}</div>
          <div className="metric__label t-caption">{label}</div>
        </div>
      </div>
    );
  }

  /* ── ExtraMetric (inline ключ:значение) ── */
  function ExtraMetric({ label, value, icon, className }) {
    return (
      <span className={'extra-metric' + (className ? ' ' + className : '')}>
        {icon && <Icon name={icon} size={13} />}
        <span className="extra-metric__key t-caption">{label}</span>
        <span className="extra-metric__val num">{value ?? '—'}</span>
      </span>
    );
  }

  /* ── MiniBar ── */
  function barTone(pct) { return pct >= 95 ? 'bad' : pct >= 80 ? 'warn' : 'ok'; }
  function MiniBar({ used = 0, limit = 100, className }) {
    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    return (
      <div className={'mini-bar' + (className ? ' ' + className : '')} role="progressbar"
        aria-valuenow={used} aria-valuemax={limit}>
        <span className={'mini-bar__fill mini-bar__fill--' + barTone(pct)} style={{ width: pct + '%' }} />
      </div>
    );
  }

  /* ── UsageBar ──
     limit === null → безлимит (∞); иначе шкала used/limit с порогом цвета.
     fmt(v) — опц. форматтер значения (напр. секунды → «2 ч 0 мин»). */
  function UsageBar({ label, used = 0, limit = 100, unit = '', fmt, className }) {
    const unlimited = limit == null;
    const pct = unlimited ? 0 : (limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0);
    const show = (v) => (fmt ? fmt(v) : (v + unit));
    return (
      <div className={'usage-bar' + (className ? ' ' + className : '')}>
        <div className="usage-bar__head row between">
          <span className="t-label">{label}</span>
          <span className="usage-bar__nums num">
            {show(used)} <span className="text-mute">/ {unlimited ? '∞' : show(limit)}</span>
          </span>
        </div>
        {unlimited ? (
          <div className="mini-bar mini-bar--unlimited" role="progressbar" aria-valuenow={used}>
            <span className="mini-bar__fill mini-bar__fill--ok" style={{ width: '100%', opacity: 0.35 }} />
          </div>
        ) : (
          <MiniBar used={used} limit={limit} />
        )}
        {unlimited ? (
          <div className="usage-bar__warn t-caption text-dim"><Icon name="check" size={12} /> Безлимитный тариф</div>
        ) : pct >= 80 && (
          <div className={'usage-bar__warn t-caption text-' + (pct >= 95 ? 'danger' : 'dim')}>
            <Icon name="alert-triangle" size={12} /> Использовано {pct}% квоты
          </div>
        )}
      </div>
    );
  }

  /* ── ScreenHeader ── */
  function ScreenHeader({ eyebrow, title, subtitle, serif = false, align = 'left', compact = false, className }) {
    return (
      <header className={'screen-header screen-header--' + align + (compact ? ' screen-header--compact' : '') + (className ? ' ' + className : '')}>
        {eyebrow && <div className="t-eyebrow screen-header__eyebrow">{eyebrow}</div>}
        <h1 className={serif ? 't-display' : 't-h1'}>{title}</h1>
        {subtitle && <p className="screen-header__sub t-body text-dim">{subtitle}</p>}
      </header>
    );
  }

export { Stat };
export { Metric };
export { ExtraMetric };
export { MiniBar };
export { UsageBar };
export { ScreenHeader };
export { barTone };
